<script setup>
import { ref, watch, computed } from 'vue'
import FloatingDialog from '@/components/shared/FloatingDialog.vue'
import { useDataStore } from '@/stores/data'
import { useAppStore } from '@/stores/app'
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue'
import ContactView from '@/components/shared/ContactView.vue'

const dataStore = useDataStore()
const appStore = useAppStore()

const profileDialog = ref(null)

const emit = defineEmits(['logout', 'editProfile', 'settings'])

const consentsEnabled = computed({
	get: () => appStore.hasNeededConsents(),
	set: (value) => {},
})

const toggleConsent = async () => {
	await appStore.toggleConsents()
	if (!appStore.hasNeededConsents()) {
		if (appStore.currentPage != 'onboarding' && appStore.currentPage != 'permissions') {
			appStore.setPage('onboarding')
		}
	}
}

const openPrivacyPolicy = () => {
	kettle.openUrl('http://opinion.no/rvu/privacypolicy')
}

const openPrivacyDashboard = async () => {
	const url = await kettle.getPrivacyDashboardUrl()
	kettle.openUrl(url)
}

const openAppSettings = async () => {
	if (dataStore.platform === 'web') {
		if (window.self !== window.top) {
			alert('Lenking til appinnstillinger er bare tilgjengelig i appen, men du kan simulere dette ved å endre tillatelser i sidemenyen.')
			window.parent?.openSidebar()
		} else {
			alert('Lenking til appinnstillinger er bare tilgjengelig i appen.')
		}
	} else {
		kettle.openUrl('app-settings:')
	}
}

const formatDateTime = (date) => {
	// Normalize string input
	if (typeof date === 'string') {
		date = new Date(date)
	}

	// Must be a valid Date
	if (!(date instanceof Date) || isNaN(date.getTime())) {
		return ''
	}
	const formatter = new Intl.DateTimeFormat('nb-NO', {
		day: 'numeric',
		month: 'long',
		hour: 'numeric',
		minute: 'numeric',
	})
	let formattedDate = formatter.format(date)
	return formattedDate.replace(/(\d+)/, '$1')
}

const openUrl = (url) => {
	if (dataStore.platform === 'web') {
		window.open(url, '_blank')
	} else {
		kettle.openUrl(url)
	}
}

const title = computed(() => {
	var title = 'RVU Reisevaner'
	if (dataStore.appUserAgent?.appVersion) title += `<span>${dataStore.appUserAgent.appVersion}</span>`
	return title
})

const profileAvailable = computed(() => {
	return dataStore.respondentAltId && dataStore.respondentId
})

const testerModeEnabled = computed({
	get: () => appStore.testerMode,
	set: (value) => {
		if (value !== appStore.testerMode) {
			appStore.toggleTesterMode()
		}
	},
})

const handleTesterModeLongPress = () => {
	appStore.toggleTesterMode()
}

const toggleTesterMode = () => {
	appStore.toggleTesterMode()
}
</script>

<template>
	<FloatingDialog ref="profileDialog" :disabled="!profileAvailable" v-model:expanded="appStore.expandedProfile" :title="title" icon="fa-regular fa-user" position="top-left" :z-index="2000" :long-press-duration="2000" @long-press="handleTesterModeLongPress">
		<template #subtitle>
			<div class="app-version">
				<strong>Revisjon {{ appStore.version }} </strong> - {{ appStore.version_date }}
				<em v-if="dataStore.appUserAgent && dataStore.platform == 'web'"><br />SDK {{ dataStore.appUserAgent.sdkVersion }} - {{ dataStore.appUserAgent.app }}</em>
			</div>
		</template>

		<div class="profile-content">
			<div class="app-version"></div>

			<h4 class="mt-7 mb-4">Samtykker og personvern</h4>
			<div class="profile-actions">
				<div @click.stop="toggleConsent()" class="action-box">
					<strong>Samtykke til databehandling</strong>
					<div class="flex">
						<div>
							<div class="text-sm">Samtykke for å samle data om posisjon og bevegelse for å gjøre reiseanalyse</div>
							<div class="text-sm opacity-60 mt-1">
								<template v-if="consentsEnabled"> Samtykket ble gitt {{ formatDateTime(appStore.consentsGrantedDate) }} </template>
								<template v-else> Samtykket ble trukket {{ formatDateTime(appStore.consentsRevokedDate) }} </template>
							</div>
						</div>
						<ToggleSwitch class="ml-4" small v-model="consentsEnabled"></ToggleSwitch>
					</div>
				</div>
				<div @click="openPrivacyPolicy()" class="action-box">
					<strong>Personvernerklæring</strong>
					<div class="flex">
						<div class="text-sm">Les personvernerklæring for denne appen og den nasjonale reisevaneundersøkelsen.</div>
						<i class="fa-solid fa-chevron-right"></i>
					</div>
				</div>
				<div @click.stop="openPrivacyDashboard()" class="action-box">
					<strong>Kontrollpanel for datainnsamling</strong>
					<div class="flex">
						<div class="text-sm">Du kan se og laste ned data som blir samlet om din reisevaner.</div>
						<i class="fa-solid fa-chevron-right"></i>
					</div>
				</div>
				<div @click.stop="openAppSettings()" class="action-box">
					<strong>Innstillinger</strong>
					<div class="flex">
						<div class="text-sm">Se eller endre innstillinger og tilganger du har gitt appen.</div>
						<i class="fa-solid fa-chevron-right"></i>
					</div>
				</div>
				<div v-if="appStore.testerMode" @click.stop="toggleTesterMode()" class="action-box tester-mode-action" role="status">
					<strong>Testermodus</strong>
					<div class="flex">
						<div class="text-sm">Når denne er aktivert kan du kommentere etapper og reiser</div>
						<div class="ml-4" @click.stop>
							<ToggleSwitch small v-model="testerModeEnabled"></ToggleSwitch>
						</div>
					</div>
				</div>
			</div>

			<ContactView />
		</div>
	</FloatingDialog>
</template>

<style scoped>
.app-version {
	margin-top: -0.5rem;
	font-size: 0.8rem;
	opacity: 0.6;
}

.tester-mode-action {
	border: 1px solid #ffd27d;
	background: #fff7e6;
}
</style>
